import { Component, ElementRef, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Lightbox } from 'ngx-lightbox';

import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { FileModel } from '../../models/file.model';
import { FileService } from '../../services/file.service';
import { RelationImageService } from '../../services/relation-image.service';
import { MediaComponent } from '../../../forms/elements/components/media/media.component';
import { ToastService } from '../../../toast/services/toast.service';
import { ConfigService } from '../../../config/config.service';
import { ApiCollection } from '../../../api/collection';
import { Model } from '@kaiser-software/chassis-core';

@Component({
    selector: 'chassis-core-files-image-relation-plugin',
    templateUrl: './image-relation.plugin.html',
    styleUrls: ['./image-relation.plugin.scss'],
    providers: [RelationImageService, FileService]
})
/**
 * The component to represent the image relations
 */
export class ImageRelationPlugin implements OnInit, OnDestroy {
    @ViewChild(MediaComponent) mediaComponent: MediaComponent;
    @ViewChild('panel') panel: ElementRef;

    /**
     * The form handler which processes the main form
     */
    @Input() form; // form handler which processes the main form

    /**
     * The model subject to subscribe on in order to check whether the imageable is loaded
     */
    @Input() modelSubject;

    /**
     * The ActivatedRoute instance.
     */
    protected activatedRoute: ActivatedRoute;

    /**
     * The AuthenticationService instance.
     */
    protected authenticationService: AuthenticationService;

    /**
     * The ChasssiService instance.
     */
    protected config: ConfigService;

    /**
     * The FileService instance.
     */
    protected fileService: FileService;

    /**
     * The Lightbox instance.
     */
    protected lightbox: Lightbox;

    /**
     * The ToastService instance.
     */
    protected toastService: ToastService;

    /**
     * The RelationImageService instance.
     */
    protected relationImageService: RelationImageService;

    /**
     * The model of the image-able
     */
    public model;

    /**
     * All the images related to this imageable.
     */
    public images;

    /**
     * The plural form of the url for the imageable service
     *
     * @type {string}
     */
    private serviceModelUrl: string = '';

    /**
     * The model id of the url for the imageable service
     *
     * @type {string}
     */
    private serviceModelId: string = '';

    /**
     * The album for Lightbox
     *
     * @type {any[]}
     */
    private lightboxAlbum: Array<any> = [];

    /**
     * The settings of the file input/dropzone
     */
    public mediaSettings;

    /**
     * The loading indicator
     *
     * @type {boolean}
     */
    public loading = true;

    constructor(protected injector: Injector) {
        this.activatedRoute        = this.injector.get(ActivatedRoute);
        this.authenticationService = this.injector.get(AuthenticationService);
        this.config                = this.injector.get(ConfigService);
        this.fileService           = this.injector.get(FileService);
        this.lightbox              = this.injector.get(Lightbox);
        this.toastService          = this.injector.get(ToastService);
        this.relationImageService  = this.injector.get(RelationImageService);

        this.setMediaSettings();
    }

    /*
     * Init
     */
    ngOnInit() {
        this.modelSubject.subscribe(model => {
            this.loading = true;

            this.model = model;

            this.setupServiceVariables();

            if (this.model.exists()) {
                this.relationImageService.all(false, {
                    pluralModel: this.serviceModelUrl,
                    idModel: this.serviceModelId
                }).subscribe(images => {
                    this.images = images;

                    images.forEach(image => {
                        this.addImageToLightboxAlbum(image);
                    });

                    this.loading = false;
                });
            } else {
                this.setupProcessingCallback();

                this.images = new ApiCollection([], new FileModel, this.relationImageService);

                this.loading = false;
            }
        });
    }

    /**
     * Process data with input from the model coming from the main form handler
     */
    protected setupProcessingCallback() {
        if (this.form) {
            this.form.success((model) => {
                return new Promise((resolve, reject) => {
                    if (this.images.length > 0) {
                        // Set the id for the service url
                        this.serviceModelId = model.id;
                        this.setServiceVariables();

                        this.images.forEach(image => {
                            this.loading = true;

                            this.images.post(image).subscribe((savedFile: FileModel) => {
                                // console.log('Saved: ', savedFile);

                                this.loading = false;

                                this.toastService.success('success', 'Image saved successfully', {element: this.panel.nativeElement});

                                resolve();
                            }, error => {
                                console.error('Error: ', error);

                                this.loading = false;

                                this.toastService.error('error', 'Failed to save image', {element: this.panel.nativeElement});

                                reject();
                            });
                        });
                    } else {
                        resolve();
                    }
                });
            });
        } else {
            console.error('There is no form handler supplied.');
        }
    }

    /**
     * Open the file browser to select an image
     */
    protected openFileBrowser() {
        this.mediaComponent.openFileBrowser();
    }

    /**
     * The event that will be executed when the media component is uploading files
     */
    protected mediaUploading() {
        this.loading = true;
    }

    /**
     * The event that will be executed when the media component is done uploading files
     */
    protected mediaUploaded() {
        this.loading = false;
    }

    /**
     * The event that will be executed when a image is uploaded
     *
     * @param {FileModel} image
     */
    protected addImage(image) {
        if (this.model.exists() === false) {
            this.images.push(image);

            this.addImageToLightboxAlbum(image);

            this.toastService.info('info', 'Image added successfully', {element: this.panel.nativeElement});
        } else {
            this.loading = true;

            this.images.post(image).subscribe(savedImage => {
                // console.log('Saved: ', savedImage);

                this.addImageToLightboxAlbum(image);

                this.loading = false;

                this.toastService.success('success', 'Image saved successfully', {element: this.panel.nativeElement});
            }, error => {
                console.error('Error: ', error);

                this.loading = false;

                this.toastService.error('error', 'Failed to save image', {element: this.panel.nativeElement});
            });
        }
    }

    /**
     * View the selected image
     *
     * @param {number} index
     */
    protected viewImage(index: number) {
        this.lightbox.open(this.lightboxAlbum, index, {
            showImageNumberLabel: true,
            wrapAround: true,
            disableScrolling: true
        });
    }

    /**
     * Download all images.
     */
    protected downloadAllImages() {
        this.images.forEach(image => {
            this.downloadImage(image);
        });
    }

    /**
     * Download the selected image
     *
     * @param image
     */
    protected downloadImage(image) {
        const url = this.config.get('api_url') + '/' + this.fileService.restEndpoint + '/' + image.id + '/download?token=' + this.authenticationService.getToken();
        window.open(url, '_blank');

        this.toastService.success('success', 'Image downloaded successfully', {element: this.panel.nativeElement});
    }

    /**
     * The event that will be executed when a image is removed through the trash icon
     *
     * @param {FileModel} image
     */
    protected removeImage(image: FileModel) {
        if (this.model.exists() === false) {
            this.images.forget('id', image.id);

            this.removeImageFromLightboxAlbum(image);

            this.toastService.info('info', 'Image removed successfully', {element: this.panel.nativeElement});
        } else {
            this.loading = true;

            this.images.delete(image.id).subscribe(deletedImage => {
                // console.log('Deleted: ', deletedImage);

                this.removeImageFromLightboxAlbum(image);

                this.loading = false;

                this.toastService.success('success', 'Image deleted successfully', {element: this.panel.nativeElement});
            }, error => {
                console.error('Error: ', error);

                this.loading = false;

                this.toastService.error('error', 'Failed to delete image', {element: this.panel.nativeElement});
            });
        }
    }

    /**
     * Add image to Lightbox album
     *
     * @param image
     */
    private addImageToLightboxAlbum(image) {
        const lightboxImage = {
            src: image.url,
            caption: 'Product image',
            thumb: image.thumbnail_url
        };

        this.lightboxAlbum.push(lightboxImage);
    }

    /**
     * Remove an image from the Lightbox album
     *
     * @param image
     */
    private removeImageFromLightboxAlbum(image) {
        this.lightboxAlbum.forEach(lightboxImage => {
            if (lightboxImage.url === image.url) {
                const index = this.lightboxAlbum.indexOf(lightboxImage);
                this.lightboxAlbum.splice(index, 1);
            }
        });
    }

    /**
     * Set the settings of the file input/dropzone
     */
    private setMediaSettings() {
        this.mediaSettings = {
            // fileType: ['jpg', 'JPG', 'jpeg', 'JPEG', 'png', 'PNG', 'gif', 'GIF', 'bmp', 'BMP'],
            // mimeType: ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'],
            multiple: true
        };
    }

    /**
     * Get the current route url segments
     */
    private setupServiceVariables() {
        this.modelSubject.subscribe((model: Model) => {
            if (model.id && model.id > 0) {
                this.serviceModelId = model.id + '';
            }

            this.serviceModelUrl = model._service.restEndpoint;

            this.setServiceVariables();
        });

    }

    /**
     * Set the universal service variables
     */
    private setServiceVariables() {
        this.relationImageService.defaultParams = {pluralModel: this.serviceModelUrl, idModel: this.serviceModelId};
    }

    /*
     * Cleanup
     */
    ngOnDestroy() {
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/files/plugins/image-relation/image-relation.plugin.ts